import './Graphic.scss';
import * as THREE from 'three';
import React, { useEffect, useRef } from 'react';
import steelGridMesh from '../../../images/mesh_steelgrid.png';
import heaMesh from '../../../images/mesh_hea.png';
import dtMesh from '../../../images/mesh_dt.png';
import macArmourMesh from '../../../images/mesh_macarmour.png';
import macmatHsMesh from '../../../images/mesh_macmat_hs.png';
import macmatRMesh from '../../../images/mesh_macmat_r.png';
import macringMesh from '../../../images/mesh_macring.png';
import gridImage from '../../../images/grid.svg';
import chiodoImg from '../../../images/chiodo.png';
import streetTextureImg from '../../../images/street_texture.jpg';
import wallTexture from '../../../images/texture.jpg';
import groundTexture from '../../../images/grey_rock_ground.jpg';
import { Grid } from '@mui/material';
import { IGraphicProps } from './IGraphic';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import * as TWEEN from '@tweenjs/tween.js';
import { RootState } from '../../../../reduxService';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../reduxService/hooks';
import { use } from 'i18next';
import { useLocation } from 'react-router-dom';

export const Graphic = (props: IGraphicProps) => {
    const mountRef = useRef<HTMLDivElement>(null);
    const sceneRef = useRef<THREE.Scene>();
    const cameraRef = useRef<THREE.PerspectiveCamera>();
    const rendererRef = useRef<THREE.WebGLRenderer>();
    const [img, setImg] = React.useState('');
    const location = useLocation();

    const committedProject = useSelector((state: RootState) => state.Macro1.committedProject);

    const inResults = location.pathname.split('/')[1] === 'results';

    const dispatch = useAppDispatch();

    useEffect(() => {
        // Initialize Three.js components when the component mounts
        if (!mountRef.current) return;


        let slopeInclination = 0;
        let horizontalSpacing = 0;
        let verticaSpacing = 0;


        //console.log(committedProject.data);

        if (committedProject.data && committedProject.data.rockSlope) {
            slopeInclination = committedProject.data.rockSlope.inclination;
        }

        if (committedProject.data && committedProject.data.anchorBar) {
            horizontalSpacing = committedProject.data.anchorBar.horizontalSpacing;
            verticaSpacing = committedProject.data.anchorBar.verticalSpacing;
        }

        // Create a scene
        const scene = new THREE.Scene();
        sceneRef.current = scene;

        const width = (mountRef.current.offsetWidth * 0.9) > 700 ? (mountRef.current.offsetWidth * 0.9) : 700;
        const height = (mountRef.current.offsetHeight * 0.9) > 700 ? (mountRef.current.offsetHeight * 0.9) : 700;

        // Create a camera
        const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
        camera.position.set(2.2, 1.5, 3.1);
        cameraRef.current = camera;

        // Create a renderer
        const renderer = new THREE.WebGLRenderer({ alpha: true });
        // Set the size of the renderer to the size of its container
        renderer.setSize(width, height);
        rendererRef.current = renderer;

        // Append the renderer to the mount point
        mountRef.current.appendChild(renderer.domElement);

        const controls = new OrbitControls(camera, renderer.domElement);

        controls.enableRotate = false;
        controls.enableZoom = false;
        controls.minDistance = 3;
        controls.maxDistance = 5;

        document.getElementById('btnZoomIn')?.addEventListener("click", event => {
            tween('in');
        })

        document.getElementById('btnZoomOut')?.addEventListener("click", event => {
            tween('out');
        })

        document.getElementById('btnZoomReset')?.addEventListener("click", event => {
            tween('reset');
        })

        function tween(type: string) { // in - true, out - false

            let dir = new THREE.Vector3();
            camera.getWorldDirection(dir);
            dir.negate();
            let dist = controls.getDistance();

            let desiredDistance = 0;

            if (type === 'in') {
                desiredDistance = controls.minDistance;
            } else if (type === 'out') {
                desiredDistance = controls.maxDistance;
            } else if (type === 'reset') {
                desiredDistance = controls.minDistance + 1;
            }


            //console.log(dist, desiredDistance);
            
            new TWEEN.Tween({val: dist})
              .to({val: desiredDistance}, 1000)
              .onUpdate(val => {
                camera.position.copy(controls.target).addScaledVector(dir, val.val);
              })
              .start();
          }

        renderer.setClearColor(0x000000, 0);

        // Load the texture image
        const textureLoader = new THREE.TextureLoader();

        createRockSlopeShape(slopeInclination, scene, renderer, textureLoader);

        createRockSlopeBorder(scene, slopeInclination);

        createPlane(1, 1.5, calculateTouchPointX(slopeInclination) + 0.5, 0, 0.75, Math.PI / 2, scene, renderer, textureLoader);

        let netImage = selectNetTexture(committedProject.data?.mesh?.meshTypeName);

        createNet(scene, textureLoader, renderer, slopeInclination, netImage);

        createPlaneAnchors(scene, renderer, textureLoader, horizontalSpacing, verticaSpacing, slopeInclination);

        // Animation loop
        const animate = () => {
            requestAnimationFrame(animate);
            TWEEN.update();
            renderer.render(scene, camera);
            saveCanvasAsImage(renderer.domElement);
        };

        if (horizontalSpacing != 0 && verticaSpacing != 0 && slopeInclination != 0) {
            animate();
        }

        // Cleanup function
        return () => {
            mountRef.current?.removeChild(renderer.domElement);
        };
    }, [committedProject]);

    const saveCanvasAsImage = (domElement: HTMLCanvasElement) => {
        const canvas = domElement;
        if (canvas) {

            const src = canvas.toDataURL();
            // Save the image
            localStorage.setItem('graphic', src);
        }
    };

    const calculateTouchPointX = (betaDegrees: number): number => {
        // Convert beta from degrees to radians
        const betaRadians = THREE.MathUtils.degToRad(betaDegrees);

        // Calculate x coordinate using the equation x = 1 / tan(beta)
        const xCoordinate = 1 / Math.tan(betaRadians);

        return xCoordinate;
    };

    const calculateX = (y: number, angleDegrees: number) => {
        // Convert angle from degrees to radians
        let angleRadians = angleDegrees * (Math.PI / 180);

        // Calculate x using the formula x = y / tan(angle)
        let x = (1.1 - y) / Math.tan(angleRadians);

        return x;
    }

    const calculateScaledValue = (value: number, slopeTotalHeight: number): number => {
        let highestPoint = 1;
        let scalingFactor = highestPoint / slopeTotalHeight;
        return value * scalingFactor;
    };

    const getRockSlopeShape = (slopeInclination: number) => {
        const shape = new THREE.Shape();
        shape.lineTo(0, 1);
        shape.lineTo(calculateTouchPointX(slopeInclination), 0);
        shape.lineTo(0, 0);

        return shape;
    };

    const createRockSlopeShape = (slopeInclination: number, scene: THREE.Scene, renderer: THREE.WebGLRenderer, textureLoader: THREE.TextureLoader) => {

        const shape = getRockSlopeShape(slopeInclination);

        // Define the extrusion settings
        const extrudeSettings = {
            steps: 1,
            depth: 1.5, // The depth of the extrusion
            bevelEnabled: false,
        };

        // Create the shape
        const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

        const wallTextureMaterial = textureLoader.load(wallTexture, () => {
            wallTextureMaterial.wrapS = THREE.RepeatWrapping;
            wallTextureMaterial.wrapT = THREE.RepeatWrapping;
            wallTextureMaterial.repeat.set(4, 4); // Set the desired number of repetitions
            wallTextureMaterial.anisotropy = renderer.capabilities.getMaxAnisotropy();
            wallTextureMaterial.colorSpace = THREE.SRGBColorSpace;
        });

        const groundTextureMaterial = textureLoader.load(groundTexture, () => {
            groundTextureMaterial.wrapS = THREE.RepeatWrapping;
            groundTextureMaterial.wrapT = THREE.RepeatWrapping;
            groundTextureMaterial.repeat.set(4, 4); // Set the desired number of repetitions
            groundTextureMaterial.anisotropy = renderer.capabilities.getMaxAnisotropy();
            groundTextureMaterial.colorSpace = THREE.SRGBColorSpace;
        });

        // Create material with texture
        const wallMaterial = new THREE.MeshBasicMaterial({ map: wallTextureMaterial });
        const groundMaterial = new THREE.MeshBasicMaterial({ map: groundTextureMaterial });

        const mesh = new THREE.Mesh(geometry, [groundMaterial, wallMaterial, wallMaterial, wallMaterial, wallMaterial, wallMaterial]);

        // Add the mesh to your scene
        scene.add(mesh);
    };

    const createRockSlopeBorder = (scene: THREE.Scene, slopeInclination: number) => {
        //Create the line
        const points = [];
        points.push(new THREE.Vector3(0, 1, 0));
        points.push(new THREE.Vector3(calculateTouchPointX(slopeInclination), 0, 0));
        points.push(new THREE.Vector3(calculateTouchPointX(slopeInclination), 0, 1.5));
        points.push(new THREE.Vector3(0, 1, 1.5));


        const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
        const lineMaterial = new THREE.LineBasicMaterial({ color: 0x8B4513 }); // Brown color
        const line = new THREE.Line(lineGeometry, lineMaterial);

        scene.add(line);
    };

    const createPlane = (width: number, height: number, x: number, y: number, z: number, rotationX: number, scene: THREE.Scene, renderer: THREE.WebGLRenderer, textureLoader: THREE.TextureLoader) => {

        const streetTexture = textureLoader.load(streetTextureImg, () => {

            streetTexture.wrapS = THREE.RepeatWrapping;
            streetTexture.wrapT = THREE.RepeatWrapping;
            streetTexture.repeat.set(1, 1); // Set the desired number of repetitions
            streetTexture.anisotropy = renderer.capabilities.getMaxAnisotropy();
            streetTexture.colorSpace = THREE.SRGBColorSpace;
        });

        // Create a material
        const planeMaterial = new THREE.MeshBasicMaterial({ map: streetTexture, side: THREE.DoubleSide, transparent: false }); // Green color
        const planeGeometry = new THREE.PlaneGeometry(width, height);

        const plane = new THREE.Mesh(planeGeometry, planeMaterial);

        plane.position.x = x; // Half the depth of the extrusion
        plane.position.y = y; // Half the depth of the extrusion
        plane.position.z = z; // Half the depth of the extrusion
        plane.rotation.x = rotationX; // Rotate the plane to be horizontal

        scene.add(plane);
    };

    const selectNetTexture = (meshTypeName?: string) => {
        //select the substring till the first space

        let meshType = meshTypeName ? meshTypeName?.split(' ')[0].toLocaleLowerCase() : 'default';
        let meshSubType = meshTypeName ? meshTypeName?.split(' ')[1].toLocaleLowerCase() : null;

        switch (meshType) {
            case 'steelgrid':
                return steelGridMesh;
            case 'hea':
                return heaMesh;
            case 'dt':
                return dtMesh;
            case 'macring':
                return macringMesh;
            case 'macmat':
                return meshSubType == 'hs' ? macmatHsMesh : macmatRMesh;
            case 'macarmour':
                return macArmourMesh;
            default:
                return steelGridMesh;
        }
    }

    const createNet = (scene: THREE.Scene, textureLoader: THREE.TextureLoader,
        renderer: THREE.WebGLRenderer, slopeInclination: number, netImage: string) => {

        let point1 = new THREE.Vector3(0, 0, 0);
        let point3 = new THREE.Vector3(0, 1, 0);
        let point2 = new THREE.Vector3(calculateTouchPointX(slopeInclination), 0, 0);

        const rockSlopeShape = getRockSlopeShape(slopeInclination);

        const points = [];
        points.push(new THREE.Vector3(0, 1, 0));
        points.push(new THREE.Vector3(0, 1, 1));
        points.push(new THREE.Vector3(calculateTouchPointX(slopeInclination), 0, 0));
        points.push(new THREE.Vector3(calculateTouchPointX(slopeInclination), 0, 1));


        const shape = new THREE.Shape();
        shape.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++) {
            shape.lineTo(points[i].x, points[i].y);
        }
        shape.lineTo(points[0].x, points[0].y); // Close the shape

        // Define extrusion settings
        const extrudeSettings = {
            depth: 1.5, // Depth of the extrusion
            bevelEnabled: false, // Disable bevel for simplicity
            bevelSegments: 10,
        };

        // Create the geometry by extruding the shape
        const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

        const netTexture = textureLoader.load(netImage, () => {

            netTexture.wrapS = THREE.RepeatWrapping;
            netTexture.wrapT = THREE.RepeatWrapping;
            netTexture.repeat.set(8, 8); // Set the desired number of repetitions
            netTexture.anisotropy = renderer.capabilities.getMaxAnisotropy();
            netTexture.colorSpace = THREE.SRGBColorSpace;
        });

        // Create a material
        const material = new THREE.MeshBasicMaterial({ map: netTexture, side: THREE.DoubleSide, transparent: true }); // Green color

        // Create a mesh using the geometry and material
        const mesh = new THREE.Mesh(geometry, material);
        mesh.renderOrder = 1;

        // Add the mesh to the scene
        scene.add(mesh);
    };

    const createAnchors = (scene: THREE.Scene, renderer: THREE.WebGLRenderer, textureLoader: THREE.TextureLoader, x: number, y: number, z: number, scaledHorizontalSpacing: number) => {

        const anchorTexture = textureLoader.load(chiodoImg, () => {
            anchorTexture.wrapS = THREE.RepeatWrapping;
            anchorTexture.wrapT = THREE.RepeatWrapping;
            anchorTexture.repeat.set(1, 1); // Set the desired number of repetitions
            anchorTexture.anisotropy = renderer.capabilities.getMaxAnisotropy();
            anchorTexture.colorSpace = THREE.SRGBColorSpace;
        });

        // Create a material
        const anchorMaterial = new THREE.MeshBasicMaterial({ map: anchorTexture, transparent: true });

        let anchorSize = scaledHorizontalSpacing > 0.1 ? 0.1 : scaledHorizontalSpacing < 0.03 ? 0.03 : scaledHorizontalSpacing;

        const anchorGeometry = new THREE.PlaneGeometry(0.08, 0.08);

        const mesh = new THREE.Mesh(anchorGeometry, anchorMaterial);

        mesh.position.set(x, y, z);
        mesh.rotation.y = Math.PI / 2;
        mesh.renderOrder = 3;

        scene.add(mesh);
    }

    function createPlaneAnchors(scene: THREE.Scene, renderer: THREE.WebGLRenderer, textureLoader: THREE.TextureLoader, horizontalSpacing: number, verticaSpacing: number, slopeInclination: number) {

        let scaledHorizontalSpacing = calculateScaledValue(horizontalSpacing, 12);
        let scaledVerticaSpacing = calculateScaledValue(verticaSpacing, 12);

        //console.log(scaledHorizontalSpacing, scaledVerticaSpacing);

        let y = 0.95;
        let z = 1.45;
        let x = calculateX(y, slopeInclination);

        for (let i = 0; i < 1000; i++) {

            if (i == 0) {
                createAnchors(scene, renderer, textureLoader, x, y, z, scaledHorizontalSpacing);
                continue;
            }

            if ((z - scaledHorizontalSpacing) <= 0.1) {

                z = 1.45; // Reset z to the start of the row

                if (y - scaledVerticaSpacing >= 0.1) {
                    y -= scaledVerticaSpacing; // Move to the next row
                    x = (calculateX(y, slopeInclination)); // Reset x to the start of the row
                    //console.log("X", x);
                    createAnchors(scene, renderer, textureLoader, x, y, z, scaledHorizontalSpacing);
                } else {
                    break;
                }

            } else {
                z -= scaledHorizontalSpacing; // Update z if necessary
                createAnchors(scene, renderer, textureLoader, x, y, z, scaledHorizontalSpacing);
            }
        }
    }

    return (
        <Grid container id='container' spacing={2} style={{ background: `repeat url(${gridImage})` }}>
            <Grid ref={mountRef} id='graphic' item md={11} xs={11} className='canvas-container'></Grid>
            {!inResults && (
            <Grid item md={1} xs={1} id="zoom">
                <button type='button' id="btnZoomIn"> <i className='fa-solid fa-plus' /> </button>
                <button id="btnZoomOut"><i className='fa-solid fa-minus' /> </button>
                <button id="btnZoomReset"><i className='fa-solid fa-expand' /> </button>
            </Grid>
    )}
        </Grid>
    );
};

export default Graphic;
